<template>
  <div class="flex flex-col gap-3">
    <div class="flex items-center justify-between">
      <button
        type="button"
        class="flex items-center gap-2 px-3 py-2 bg-transparent border-none text-vault-blue-400 font-semibold text-sm cursor-pointer rounded-lg transition-all duration-200 hover:bg-vault-blue-400/8"
        @click="handleBack"
      >
        <ArrowLeft :size="20" />
        {{ $t('common.back') }}
      </button>
    </div>

    <!-- Transaction Created View -->
    <div v-if="transaction" class="flex flex-col gap-4">
      <div class="p-4 bg-green-500/8 border border-green-500/20 rounded-lg">
        <div class="flex items-center gap-2 mb-2">
          <CheckCircle2 :size="20" class="text-green-500" />
          <span class="font-semibold text-vault-text-primary">{{ $t('depositWidget.changelly.transactionCreated') }}</span>
        </div>
        <p class="text-sm text-vault-text-secondary m-0">
          {{ $t('depositWidget.changelly.sendCrypto') }}
        </p>
      </div>

      <!-- Payin Address -->
      <div class="p-4 bg-vault-bg-secondary rounded-lg">
        <label class="block text-sm font-semibold text-vault-text-secondary mb-2">
          {{ $t('depositWidget.changelly.payinAddress') }}
        </label>
        <div class="flex items-center gap-2">
          <code class="flex-1 p-2 bg-vault-bg-primary rounded text-xs font-mono text-vault-text-primary break-all">
            {{ transaction.payinAddress }}
          </code>
          <button
            type="button"
            class="p-2 bg-vault-blue-400/10 hover:bg-vault-blue-400/20 rounded transition-colors"
            @click="copyAddress"
          >
            <Copy :size="16" class="text-vault-blue-400" />
          </button>
        </div>
      </div>

      <!-- Transaction Details -->
      <div class="p-4 bg-vault-bg-secondary rounded-lg">
        <h3 class="text-sm font-semibold text-vault-text-primary mb-3 m-0">{{ $t('depositWidget.changelly.details') }}</h3>
        <div class="flex flex-col gap-2 text-sm">
          <div class="flex justify-between">
            <span class="text-vault-text-secondary">{{ $t('depositWidget.changelly.sendAmount') }}</span>
            <span class="font-semibold text-vault-text-primary">{{ transaction.amountExpectedFrom }} {{ fromCurrency.toUpperCase() }}</span>
          </div>
          <div class="flex justify-between">
            <span class="text-vault-text-secondary">{{ $t('depositWidget.changelly.receiveAmount') }}</span>
            <span class="font-semibold text-vault-text-primary">{{ transaction.amountExpectedTo }} {{ currency.toUpperCase() }}</span>
          </div>
          <div class="flex justify-between">
            <span class="text-vault-text-secondary">{{ $t('depositWidget.changelly.networkFee') }}</span>
            <span class="font-semibold text-vault-text-primary">{{ transaction.networkFee }} {{ fromCurrency.toUpperCase() }}</span>
          </div>
          <div class="flex justify-between">
            <span class="text-vault-text-secondary">{{ $t('depositWidget.changelly.status') }}</span>
            <span class="font-semibold text-vault-text-primary">{{ transaction.status }}</span>
          </div>
        </div>
      </div>

      <!-- Actions -->
      <div class="flex gap-3">
        <button type="button" class="btn-secondary flex-1" @click="resetTransaction">
          {{ $t('depositWidget.changelly.newExchange') }}
        </button>
        <button type="button" class="btn-primary flex-1" @click="emit('transaction-completed', transaction)">
          {{ $t('depositWidget.changelly.monitor') }}
        </button>
      </div>
    </div>

    <!-- Exchange Form -->
    <div v-else class="flex flex-col gap-3">
      <!-- Compact Currency Selection (Always Visible) -->
      <div v-if="!fromCurrency" class="p-3 bg-yellow-500/8 border border-yellow-500/20 rounded-lg">
        <div class="flex items-center gap-2 mb-2">
          <AlertCircle :size="16" class="text-yellow-500" />
          <span class="text-sm text-vault-text-primary font-semibold">{{ $t('depositWidget.changelly.pleaseSelectCurrency') }}</span>
        </div>
        <button
          type="button"
          class="btn-primary w-full py-2 text-sm"
          @click="emit('change-currency')"
        >
          {{ $t('depositWidget.changelly.selectCurrency') }}
        </button>
      </div>
      <div v-else class="flex items-center gap-2 p-3 bg-vault-bg-secondary border border-gray-200 dark:border-gray-700 rounded-lg">
        <span class="text-sm text-vault-text-secondary">{{ $t('depositWidget.changelly.from') }}</span>
        <span class="flex-1 text-sm font-semibold text-vault-text-primary">
          {{ fromCurrency.toUpperCase() }}
          <span v-if="fromNetworkSelected?.name" class="text-xs font-normal text-vault-text-secondary ml-1">{{ fromNetworkSelected.name }}</span>
        </span>
        <ArrowDown :size="16" class="text-vault-text-secondary" />
        <span class="text-sm font-semibold text-vault-text-primary">{{ currency.toUpperCase() }}</span>
        <button
          type="button"
          class="ml-2 text-xs text-vault-blue-400 hover:text-vault-blue-500 font-medium px-2 py-1 rounded hover:bg-vault-blue-400/10 transition-colors"
          @click="emit('change-currency')"
        >
          {{ $t('common.change') }}
        </button>
      </div>

      <!-- Amount Input (only show if currency selected) -->
      <div v-if="fromCurrency">
        <label class="block text-xs font-semibold text-vault-text-secondary mb-1.5">
          {{ $t('depositWidget.changelly.amount') }}
        </label>
        <input
          v-model="amount"
          type="number"
          step="any"
          :placeholder="`Min: ${minAmount || '...'}`"
          class="w-full p-2.5 bg-vault-bg-secondary border border-gray-200 dark:border-gray-700 rounded-lg text-vault-text-primary text-sm focus:outline-none focus:ring-2 focus:ring-vault-blue-400"
          @input="debouncedGetExchangeAmount"
        />
        <p v-if="minAmount" class="text-xs text-vault-text-secondary mt-1 m-0">
          {{ $t('depositWidget.changelly.minAmount') }}: {{ minAmount }} {{ fromCurrency.toUpperCase() }}
        </p>
      </div>

      <!-- Exchange Rate -->
      <div v-if="fromCurrency && exchangeAmount && !changelly.loading.value" class="p-3 bg-blue-500/8 border border-blue-500/20 rounded-lg">
        <div class="flex justify-between items-center text-sm">
          <span class="text-vault-text-secondary">{{ $t('depositWidget.changelly.youReceive') }}</span>
          <span class="font-semibold text-vault-text-primary">
            {{ exchangeAmount.amountTo }} {{ currency.toUpperCase() }}
          </span>
        </div>
        <div class="text-xs text-vault-text-secondary mt-1.5">
          {{ $t('depositWidget.changelly.networkFee') }}: {{ exchangeAmount.networkFee }} {{ fromCurrency.toUpperCase() }}
        </div>
      </div>

      <!-- Loading State -->
      <div v-if="fromCurrency && changelly.loading.value" class="flex items-center justify-center py-3">
        <div class="spinner"></div>
        <span class="ml-2 text-xs text-vault-text-secondary">{{ $t('depositWidget.changelly.loading') }}</span>
      </div>

      <!-- Error State -->
      <div v-if="fromCurrency && error" class="p-3 bg-red-500/8 border border-red-500/20 rounded-lg">
        <div class="flex items-center gap-2">
          <AlertCircle :size="16" class="text-red-500" />
          <span class="text-xs text-vault-text-primary">{{ error }}</span>
        </div>
      </div>

      <!-- Create Exchange Button -->
      <button
        v-if="fromCurrency"
        type="button"
        class="btn-primary w-full py-2.5 text-sm"
        :disabled="!canCreateExchange || changelly.loading.value"
        @click="createExchange"
      >
        {{ $t('depositWidget.changelly.createExchange') }}
      </button>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { ArrowLeft, ArrowDown, AlertCircle, Copy, CheckCircle2 } from 'lucide-vue-next';
import { useChangelly } from '../../composables/useChangelly';
import { useNotifications } from '../../composables/useNotifications';

const { t: $t } = useI18n();
const { showSuccess, showError } = useNotifications();

const props = defineProps({
  address: {
    type: String,
    required: true
  },
  currency: {
    type: String,
    default: 'USDC'
  },
  currencyInfo: {
    type: Object,
    default: null
  },
  fromCurrencySelected: {
    type: Object, // { id, code, name, ticker }
    default: null
  },
  fromNetworkSelected: {
    type: Object, // { id, name, rpcUrl }
    default: null
  }
});

const emit = defineEmits(['back', 'change-currency', 'transaction-started', 'transaction-completed']);

const changelly = useChangelly();

// State
const fromCurrency = ref(props.fromCurrencySelected?.ticker || props.fromCurrencySelected?.code || '');
const amount = ref('');
const minAmount = ref('');
const exchangeAmount = ref(null);
const transaction = ref(null);
const error = ref('');

// Can create exchange
const canCreateExchange = computed(() => {
  return fromCurrency.value && amount.value && exchangeAmount.value && !changelly.loading.value;
});

// Debounce timer
let debounceTimer = null;

// Load minimum amount on mount
onMounted(async () => {
  if (!fromCurrency.value) {
    error.value = 'No currency selected. Please go back and select a currency.';
    return;
  }

  try {
    const min = await changelly.getMinAmount(fromCurrency.value.toLowerCase(), props.currency.toLowerCase());
    minAmount.value = min;
  } catch (err) {
    console.error('Failed to get min amount:', err);
    error.value = 'Failed to get minimum amount';
  }
});

// Get exchange amount (debounced)
const debouncedGetExchangeAmount = () => {
  clearTimeout(debounceTimer);
  debounceTimer = setTimeout(async () => {
    if (!fromCurrency.value || !amount.value || parseFloat(amount.value) <= 0) {
      exchangeAmount.value = null;
      return;
    }

    try {
      error.value = '';
      const result = await changelly.getExchangeAmount({
        from: fromCurrency.value,
        to: props.currency.toLowerCase(),
        amountFrom: amount.value
      });
      exchangeAmount.value = result;
    } catch (err) {
      console.error('Failed to get exchange amount:', err);
      error.value = err.message || 'Failed to calculate exchange amount';
      exchangeAmount.value = null;
    }
  }, 500);
};

// Create exchange
const createExchange = async () => {
  if (!canCreateExchange.value) return;

  try {
    error.value = '';
    emit('transaction-started', { from: fromCurrency.value, to: props.currency, amount: amount.value });

    const result = await changelly.createTransaction({
      from: fromCurrency.value,
      to: props.currency.toLowerCase(),
      address: props.address,
      amountFrom: amount.value
    });

    transaction.value = result;
    showSuccess('Exchange created successfully!');
  } catch (err) {
    console.error('Failed to create exchange:', err);
    error.value = err.message || 'Failed to create exchange';
    showError(error.value);
  }
};

// Reset transaction
const resetTransaction = () => {
  transaction.value = null;
  fromCurrency.value = '';
  amount.value = '';
  minAmount.value = '';
  exchangeAmount.value = null;
  error.value = '';
};

// Copy address to clipboard
const copyAddress = async () => {
  if (!transaction.value) return;

  try {
    await navigator.clipboard.writeText(transaction.value.payinAddress);
    showSuccess('Address copied to clipboard!');
  } catch (err) {
    console.error('Failed to copy address:', err);
    showError('Failed to copy address');
  }
};

// Back button handler
function handleBack() {
  emit('back');
}
</script>

<style scoped>
/* Loading Spinner */
.spinner {
  @apply w-5 h-5 rounded-full border-4 border-vault-blue-400/10;
  border-top-color: rgba(102, 126, 234, 0.8);
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* Button Styles */
.btn-primary {
  @apply px-6 py-3 bg-vault-blue-400 text-white font-semibold rounded-lg transition-all duration-200;
  @apply hover:bg-vault-blue-500 active:scale-95;
  @apply disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-vault-blue-400;
}

.btn-secondary {
  @apply px-6 py-3 bg-vault-bg-secondary text-vault-text-primary font-semibold rounded-lg transition-all duration-200;
  @apply hover:bg-vault-bg-tertiary active:scale-95 border border-gray-200 dark:border-gray-700;
}
</style>
